import { useLocation } from "react-router-dom"

import { StyleAndConfigurationSubpath, SubmissionsSubpath } from "sharedTypes"

import useFeatureFlag from "services/featureFlags"
import useGuestsStore from "services/useGuestsStore"
import useSearchParams from "services/useSearchParams"

import { useShallow } from "zustand/shallow"

export const useQuery = () => new URLSearchParams(useLocation().search)

export const accountPath = () => "/account"
export const accountPlanPath = () => "/account/plan"
export const accountTeamPath = () => "/account/team"
export const accountTeamMemberPath = (teamMemberId: string) => `/account/team/members/${teamMemberId}`
export const upgradePath = (plan: string | null = null) => `/upgrade${plan ? `?plan=${plan}` : ""}`
export const eventsPath = () => "/events"
export const eventsFuturePath = () => "/events/future"
export const eventsPastPath = () => "/events/past"
export const eventPath = (eventId: string) => `/events/${eventId}`
export const eventNewDashboardPath = (eventId: string) => `${eventPath(eventId)}/dashboard`
export const eventEmailsPath = (eventId: string) => `${eventPath(eventId)}/emails`
export const eventEmailPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}`
export const eventEmailSetupPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}/setup`
export const eventEmailRecipientsPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}/recipients`
export const eventEmailEmailPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}/email`
export const eventEmailReviewPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}/review`
export const eventEmailReportPath = (eventId: string, emailId: number) => `${eventEmailsPath(eventId)}/${emailId}/report`
export const eventTablePath = (eventId: string, tableId: number) => `/events/${eventId}/seating/tables/${tableId}`
export const eventEditTablePath = (eventId: string, tableId: number) => `/events/${eventId}/seating/tables/${tableId}/edit`
export const eventSeatingPath = (eventId: string) => `${eventPath(eventId)}/seating`
export const eventBadgesPath = (eventId: string) => `${eventPath(eventId)}/badges`
export const eventFormsPath = (eventId: string, subPath?: string) => `${eventPath(eventId)}/forms${subPath ? `/${subPath}` : ""}`
export const eventFormsStyleAndConfigurationPath = (eventId: string, subPath?: StyleAndConfigurationSubpath) => `${eventFormsPath(eventId, "style-and-configuration")}${subPath ? `/${subPath}` : ""}`
export const eventFormPath = (eventId: string, formId: string) => `${eventFormsPath(eventId)}/${formId}`
export const eventEditFormPath = (eventId: string, formId: string) => `${eventFormPath(eventId, formId)}/edit`
export const eventPortalPath = (eventId: string, subPath?: StyleAndConfigurationSubpath) => `${eventPath(eventId)}/portal${subPath ? `/${subPath}` : ""}`
type PortalSubpaths = Partial<Record<StyleAndConfigurationSubpath, StyleAndConfigurationSubpath>>

const PORTAL_SUBPATHS: PortalSubpaths = {
  [StyleAndConfigurationSubpath.PortalSetup]: StyleAndConfigurationSubpath.GuestActions,
  [StyleAndConfigurationSubpath.Confirmations]: StyleAndConfigurationSubpath.GuestConfirmations,
  [StyleAndConfigurationSubpath.EmailResponders]: StyleAndConfigurationSubpath.AutomatedEmails,
}

export const useEventStyleAndConfigurationPath = () => {
  const { vanityUrls } = useFeatureFlag()

  return vanityUrls
    ? (eventId: string, subPath?: StyleAndConfigurationSubpath) => (
      eventPortalPath(eventId, subPath && (PORTAL_SUBPATHS[subPath] ?? subPath))
    )
    : eventFormsStyleAndConfigurationPath
}
export const eventWebPagePath = (eventId: string) => `${eventPath(eventId)}/webpage`
export const eventSettingsPath = (eventId: string, subPath?: "email-notifications"|"ticketing-payments"|"custom-email-domains"|"event-activities"|"kiosk-mode"|"language-templates"|"wallet-pass"|"integrations"|"team") => `${eventPath(eventId)}/settings${subPath ? `/${subPath}` : ""}`
export const eventWalletPassSettingsPath = (eventId: string) => eventSettingsPath(eventId, "wallet-pass")
export const eventWalletPassBuilderPath = (eventId: string) => `${eventWalletPassSettingsPath(eventId)}/builder`
export const eventPortalWalletPassPath = (eventId: string) => (
  eventPortalPath(eventId, StyleAndConfigurationSubpath.GuestConfirmations)
)
export const eventPortalWalletPassBuilderPath = (eventId: string) => `${eventPortalWalletPassPath(eventId)}/wallet-pass-builder`
export const eventSubmissionsPath = (eventId: string, subPath?: SubmissionsSubpath) => `${eventPath(eventId)}/submissions${subPath ? `/${subPath}` : ""}`
export const eventSubmissionPath = (eventId: string) => `${eventPath(eventId)}/submissions`
export const eventQuestionsPath = (eventId: string) => `${eventPath(eventId)}/submissions/questions`
export const eventDataCollectionFormSubmissionPath = (eventId: string, submissionId: string) => `${eventSubmissionsPath(eventId)}/${submissionId}`
export const eventDataCollectionQuestionPath = (eventId: string, questionId: string) => `${eventQuestionsPath(eventId)}/${questionId}`
export const eventTextingPath = (eventId: string) => `${eventPath(eventId)}/texts`
export const eventTextMessagePath = (eventId: string, textMessageId: string) => `${eventTextingPath(eventId)}/${textMessageId}`
export const eventTextMessageReportPath = (eventId: string, textMessageId: string) => `${eventTextingPath(eventId)}/${textMessageId}/report`
export const loginPath = () => "/login"
export const registrationPath = () => "/register"
export const completeRegistrationPath = (plan: string | null = null) => `/register/complete${plan ? `?plan=${plan}` : ""}`
export const forgotPasswordPath = () => "/passwords/new"
export const resetPasswordPath = () => "/users/password/edit"
export const confirmEmailPath = () => "/users/confirmation"
export const leadRetrievalDashboardPath = () => "/lead-retrieval-dashboard"
export const leadRetrievalRegisterPath = () => "/lead-retrieval-register"
export const iOSAppURL = "https://itunes.apple.com/us/app/diobox/id889676302"
export const AndroidAppURL = "https://play.google.com/store/apps/details?id=com.diobox.rn.android"
export const mobileAppURL = () => (/android/i.test(navigator.userAgent) ? AndroidAppURL : iOSAppURL)

// Builds the URL query string for the guests view from a coherent set of
// state fields. Combinatorial truth table:
//   - groupId is mutually exclusive with segmentId / rules — they're
//     different "filter axes" for the same purpose; picking a guest list
//     drops any chip set or active segment.
//   - segmentId AND rules co-exist when the user is editing a saved
//     segment: segmentId is the "source segment" (drives the modified `*`
//     indicator + Save changes PUT path), rules carries the live chip
//     state. Backend gives rules precedence when both are sent.
//   - Search (q) co-exists with ALL of the above. Typing in the search
//     bar narrows whatever filter axis is already active; adding a chip
//     while a search is on doesn't clear the search. Search isn't
//     persisted into Segment.rules — it stays URL-only.
const guestListFilterQuery = ({
  groupId,
  segmentId,
  rules,
  searchString: q,
}: {
  groupId: string
  segmentId?: string
  rules?: string
  searchString: string
}) => {
  const parts: string[] = []
  if (groupId) {
    parts.push(`groupId=${encodeURIComponent(groupId)}`)
  } else {
    if (segmentId) parts.push(`segmentId=${encodeURIComponent(segmentId)}`)
    // `rules` is the decoded JSON string from useSearchParams; re-encode
    // when stitching back into the URL.
    if (rules) parts.push(`rules=${encodeURIComponent(rules)}`)
  }
  if (q) parts.push(`q=${encodeURIComponent(q)}`)

  return parts.length > 0 ? parts.join("&") : null
}

export const useEventGuestPaths = () => {
  const guestFilters = useGuestsStore(
    useShallow((state) => ({
      groupId: state.groupId,
      segmentId: state.segmentId,
      searchString: state.searchString,
    })),
  )
  // Chips live in the URL — read the raw `rules=` value and pass through
  // opaquely so this hook stays unaware of the chip wire shape.
  const { rules } = useSearchParams<{ rules?: string }>()

  const basePath = (eventId: string) => `${eventPath(eventId)}/guests`
  const filter = () => guestListFilterQuery({ ...guestFilters, rules })

  const guestsPath = (eventId: string, queryParams?: string) => {
    const filterString = queryParams ?? filter()
    if (filterString) {
      return `${basePath(eventId)}?${filterString}`
    }

    return basePath(eventId)
  }

  const guestPath = (eventId: string, guestId: string, path?: string) => {
    const filterString = filter()
    let url = `${basePath(eventId)}/${guestId}`

    if (path) {
      url += `/${path}`
    }

    if (filterString) {
      url += `?${filterString}`
    }

    return url
  }

  return {
    eventGuestsPath: guestsPath,
    eventGuestPath: guestPath,
  }
}

export const eventBadgeTemplateEditPath = (eventId: string, templateId: string) => `${eventBadgesPath(eventId)}/${templateId}/edit`
export const eventBadgePrintingPath = (eventId: string) => `${eventPath(eventId)}/settings/badge-printing`
